import * as React from "react";
import {useCallback} from "react";
import {Clock} from "./Clock";
import VariocubeLogo from "./img/logo.svg";
import {SettingsItem} from "./SettingsItem";
import {IconNames, layout, useFlag} from "@variocube/cube-ui";
import {Controller, SupportInfo} from "./controller/Controller";
import {stylesheet} from "typestyle";
import {AwesomeBarItem} from "./AwesomeBarItem";
import {AwesomeBarDropdown} from "./AwesomeBarDropdown";
import {LocationCode} from "./LocationCode";
import {AWESOME_BAR_BACKGROUND, AWESOME_BAR_HEIGHT, AWESOME_BAR_ZINDEX} from "./common";
import {SupportInfoView} from "./SupportInfoView";
import {LANGUAGE_NAMES, useLocalization} from "../i18n";

interface AwesomeBarProps {
    title?: string;
    logo?: boolean;
    controller: Controller;
    onHome: () => void;
    onSettings: () => void;
    language: string;
    availableLanguages: string[];
    onSelectLanguage: (lang: string) => void;
    locationCode?: string;
    supportInfo?: SupportInfo
}

export function AwesomeBar(props: AwesomeBarProps) {
    const {
        title,
        logo,
        controller,
        onHome,
        onSettings,
        language,
        availableLanguages,
        onSelectLanguage,
        locationCode,
        supportInfo
    } = props;
    const {t} = useLocalization();

    return (
        <div className={styles.bar}>
            <Title title={title} logo={logo}/>
            <div className={layout.horizontal}>
                <AwesomeBarItem onClick={onHome} startIcon={IconNames.home} label={t("Home")}/>
                <LanguageSwitcher availableLanguages={availableLanguages} onSelectLanguage={onSelectLanguage}/>
                <SupportInfoView supportInfo={supportInfo} />
            </div>
            <div className={layout.horizontal}>
                <LocationCode code={locationCode}/>
                <SettingsItem controller={controller} onSettings={onSettings}/>
                <Clock language={language}/>
            </div>
        </div>
    );
}

interface LanguageSwitcherProps {
    availableLanguages: string[];
    onSelectLanguage: (lang: string) => void
}

function LanguageSwitcher({availableLanguages, onSelectLanguage}: LanguageSwitcherProps) {
    const {t} = useLocalization();
    const [open, setOpen, clearOpen] = useFlag(false);
    const handleSelect = useCallback((language: string) => {
        clearOpen();
        onSelectLanguage(language);
    }, [onSelectLanguage]);
    return (
        <AwesomeBarDropdown
            open={open}
            onClose={clearOpen}
            button={<AwesomeBarItem startIcon={IconNames.language} label={t("LanguageName")} onClick={setOpen}/>}
            alignContent="left"
        >
            {availableLanguages.map(lang => (
                <LanguageItem key={lang} language={lang} onSelect={handleSelect}/>
            ))}
        </AwesomeBarDropdown>
    );
}

interface LanguageItemProps {
    language: string;
    onSelect: (lang: string) => void;
}

function LanguageItem({language, onSelect}: LanguageItemProps) {
    const handleClick = useCallback(() => onSelect(language), [onSelect, language]);

    return (
        <AwesomeBarItem
            className={styles.languageItem}
            label={LANGUAGE_NAMES[language]}
            onClick={handleClick}
        />
    );
}

function Title({title, logo = true}: Pick<AwesomeBarProps, "title" | "logo">) {
    return (
        <div className={styles.title}>
            {title && (
                <div className={styles.titleText}>
                    {title}
                </div>
            )}
            {!title && logo && (
                <div className={styles.titleLogo} style={{backgroundImage: `url(${VariocubeLogo})`}}/>
            )}
        </div>
    );
}

const styles = stylesheet(({
    bar: {
        position: "fixed",
        top: 0,
        width: "100%",
        zIndex: AWESOME_BAR_ZINDEX,
        height: AWESOME_BAR_HEIGHT,
        background: AWESOME_BAR_BACKGROUND,
        color: "#ccc",
        display: "flex",
        flexFlow: "row nowrap",
        justifyContent: "space-between",
    },
    title: {
        position: "absolute",
        left: "33.333%",
        top: 0,
        height: "100%",
        width: "33.333%",
        textAlign: "center",
    },
    titleText: {
        fontWeight: "bold",
    },
    titleLogo: {
        display: "block",
        height: "100%",
        background: "no-repeat center center",
        backgroundSize: "auto 75%",
    },
    home: {},
    languageItem: {
        borderTop: "1px solid #666",
        background: "#222",
    }
}));
